package io.jsonize.web.screens.editor

import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxHeight
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.AccountTree
import androidx.compose.material.icons.filled.Code
import androidx.compose.material.icons.filled.Compare
import androidx.compose.material.icons.filled.DataObject
import androidx.compose.material.icons.filled.Edit
import androidx.compose.material3.LinearProgressIndicator
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import androidx.compose.ui.Modifier
import androidx.compose.ui.platform.LocalClipboardManager
import androidx.compose.ui.text.AnnotatedString
import androidx.compose.ui.unit.dp
import io.jsonize.web.theme.LocalThemeIsDark
import io.jsonize.web.view.JsonTreeView
import io.jsonize.web.viewmodel.AppViewModel
import io.jsonize.web.compare.LineDiff
import io.jsonize.web.services.FileExport
import io.jsonize.web.services.copyToClipboard
import io.jsonize.web.services.downloadTextFile
import io.jsonize.web.view.CompareToolbar
import io.jsonize.web.view.CompareView
import io.jsonize.web.view.GeneratorPanel
import io.jsonize.web.view.GeneratorToolbar
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.delay
import kotlinx.coroutines.launch
import kotlinx.serialization.json.JsonElement
import org.jetbrains.skia.paragraph.Alignment
import kotlin.math.roundToInt

private const val MAX_FILE_MB = 2.0
private const val MAX_TEXT_CHARS = 2 * 1024 * 1024 // ~2 MB in chars

@Composable
fun EditorLeftPane(
    appViewModel: AppViewModel,
    viewMode: EditorViewMode,
    onViewModeChange: (EditorViewMode) -> Unit,
    parsedJson: JsonElement?,
    showViewToggle: Boolean,
    lineDiffs: List<LineDiff> = emptyList(),
    externalEditorRef: androidx.compose.runtime.MutableState<EditorView?>? = null,
    stackCompareLayout: Boolean = false
) {

    val text by appViewModel.text.collectAsState()
    val compareText by appViewModel.compareText.collectAsState()
    val formatting by appViewModel.formatting.collectAsState()
    val progress by appViewModel.formatProgress.collectAsState()
    val error by appViewModel.error.collectAsState()
    val jsonError by appViewModel.jsonError.collectAsState()
    val isDark by LocalThemeIsDark.current

    val clipboard = LocalClipboardManager.current

    var localText by remember { mutableStateOf(text) }

    var jumpToOffset by remember { mutableStateOf<Int?>(null) }
    var fileProgress by remember { mutableStateOf<EditorFileProgress?>(null) }
    val editorRef = externalEditorRef ?: remember { mutableStateOf<EditorView?>(null) }

    // dY"A? NEW: streaming flag
    var isStreaming by remember { mutableStateOf(false) }

    val scope = rememberCoroutineScope()

    LaunchedEffect(text) {
        if (localText != text) localText = text
    }
    LaunchedEffect(text, isStreaming) {
        if (isStreaming) return@LaunchedEffect
        delay(350)
        if (text != appViewModel.text.value) return@LaunchedEffect
        appViewModel.validateJsonAsync()
    }

    val effectiveViewMode = if (showViewToggle) viewMode else EditorViewMode.Code

    @Composable
    fun MainEditorContent(modifier: Modifier = Modifier) {
        Column(modifier = modifier) {
            fileProgress?.let { fp ->
                if (!fp.done) {
                    FileProgressIndicator(fp)
                    Spacer(Modifier.height(8.dp))
                }
            }

            if (formatting) {
                LinearProgressIndicator(
                    progress = { progress },
                    modifier = Modifier.fillMaxWidth()
                )
            }

            if (error != null) {
                Spacer(Modifier.height(4.dp))
            }

            EditorTextArea(
                text = localText,
                onTextChange = { updated ->
                    if (updated.length > MAX_TEXT_CHARS) {
                        val kb = (updated.length.toDouble() / 1024.0).roundToInt()
                        appViewModel.updateError(
                            "Content too large (${kb} KB). Max allowed is 2048 KB (2 MB)."
                        )
                        return@EditorTextArea
                    }
                    localText = updated
                    appViewModel.updateText(updated)
                },
                lineDiffs = lineDiffs,
                errorLine = jsonError?.line,
                onDropFile = { file ->
                    scope.launch {
                        val sizeMb = file.size.toDouble() / (1024 * 1024)
                        if (sizeMb > MAX_FILE_MB) {
                            val kb = (file.size.toDouble() / 1024.0).roundToInt()
                            fileProgress = null
                            appViewModel.updateError(
                                "File too large (${kb} KB). Max allowed is 2048 KB (2 MB)."
                            )
                            return@launch
                        }

                        // start streaming
                        isStreaming = true
                        localText = ""
                        appViewModel.updateText("")
                        appViewModel.clearError()

                        try {
                            EditorFileReader.readFileProgressive(file) { chunk, prog ->
                                val newLen = localText.length + chunk.length
                                if (newLen > MAX_TEXT_CHARS) {
                                    val kb = (newLen.toDouble() / 1024.0).roundToInt()
                                    fileProgress = null
                                    appViewModel.updateError(
                                        "Content too large (${kb} KB). Max allowed is 2048 KB (2 MB)."
                                    )
                                    throw CancellationException("Content too large")
                                }

                                localText += chunk
                                appViewModel.updateText(localText)
                                fileProgress = prog
                            }

                            fileProgress = null
                        } catch (e: CancellationException) {
                            fileProgress = null
                        } catch (e: Throwable) {
                            fileProgress = null
                            appViewModel.updateError(e.message ?: "Failed to read file")
                        } finally {
                            isStreaming = false   // dY"A? enabling highlight again
                        }
                    }
                },

                onDropText = { dropped ->
                    if (dropped.length > MAX_TEXT_CHARS) {
                        val kb = (dropped.length.toDouble() / 1024.0).roundToInt()
                        appViewModel.updateError(
                            "Content too large (${kb} KB). Max allowed is 2048 KB (2 MB)."
                        )
                        return@EditorTextArea
                    }
                    localText = dropped
                    appViewModel.updateText(dropped)
                },
                isStreaming = isStreaming,
                isDark = isDark,
                placeholderText = AppViewModel.DEFAULT_JSON_PLACEHOLDER,
                modifier = Modifier.weight(1f).fillMaxWidth(),
                jumpToOffset = jumpToOffset,
                onEditorReady = { editorRef.value = it },
                onEditorDispose = { editorRef.value = null }
            )
        }
    }

    if (stackCompareLayout && effectiveViewMode == EditorViewMode.Compare) {
        Column(Modifier.fillMaxSize().padding(8.dp)) {
            Row(Modifier.weight(1f).fillMaxWidth()) {
                Column(Modifier.padding(end = 6.dp)) {
                    EditorToolbar(
                        text = localText,
                        isFormatting = formatting,
                        onFormat = { appViewModel.formatJsonAsync() },
                        onMinify = { appViewModel.minifyJsonAsync() },
                        onCancel = { appViewModel.cancelFormatting() },
                        onCopy = { },
                        onSearch = {
                            editorRef.value?.let { view ->
                                openSearchPanel(view)
                            }
                        },
                        viewMode = viewMode,
                        onViewModeChange = onViewModeChange,
                        showViewToggle = showViewToggle
                    )
                }

                MainEditorContent(
                    modifier = Modifier
                        .fillMaxHeight()
                        .weight(1f)
                )
            }

            Spacer(Modifier.height(8.dp))

            Row(Modifier.weight(1f).fillMaxWidth()) {
                Column(Modifier.padding(end = 6.dp)) {
                    CompareToolbar(
                        text = compareText,
                        format = {
                            appViewModel.formatCompareJsonAsync()
                        },
                        minify = {
                            appViewModel.minifyCompareJsonAsync()
                        },
                        clear = {
                            appViewModel.updateCompareText("")
                            appViewModel.clearCompareDiffs()
                        },
                        swap = {
                            appViewModel.swapCompareTexts()
                        },
                        copy = {
                            clipboard.setText(AnnotatedString(appViewModel.compareText.value))
                        },
                        download = {
                            FileExport.default.downloadJson(
                                "jsonize.json",
                                appViewModel.compareText.value
                            )
                        }
                    )
                }

                Column(
                    modifier = Modifier
                        .fillMaxHeight()
                        .weight(1f)
                ) {
                    CompareView(appViewModel, editorRef, 0.dp)
                }
            }

            if (showViewToggle) {
                Spacer(Modifier.height(6.dp))
                Row(Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.spacedBy(6.dp)) {
                    ViewModeButton(
                        selected = viewMode == EditorViewMode.Code,
                        onClick = { onViewModeChange(EditorViewMode.Code) },
                        icon = Icons.Default.Edit,
                        description = "Code view"
                    )
                    ViewModeButton(
                        selected = viewMode == EditorViewMode.Tree,
                        onClick = { onViewModeChange(EditorViewMode.Tree) },
                        icon = Icons.Default.AccountTree,
                        description = "Tree view"
                    )
                    ViewModeButton(
                        selected = viewMode == EditorViewMode.Compare,
                        onClick = { onViewModeChange(EditorViewMode.Compare) },
                        icon = Icons.Default.Compare,
                        description = "Compare View"
                    )
                    ViewModeButton(
                        selected = viewMode == EditorViewMode.Generator,
                        onClick = { onViewModeChange(EditorViewMode.Generator) },
                        icon = Icons.Default.Code,
                        description = "Code Generator"
                    )
                }
            }
        }
    } else {
        Row(Modifier.fillMaxSize().padding(8.dp)) {
            Column(Modifier.padding(end = 6.dp),
                horizontalAlignment = androidx.compose.ui.Alignment.CenterHorizontally) {
                if (showViewToggle) {
                    when (effectiveViewMode) {
                        EditorViewMode.Code -> {
                            EditorToolbar(
                                text = localText,
                                isFormatting = formatting,
                                onFormat = { appViewModel.formatJsonAsync() },
                                onMinify = { appViewModel.minifyJsonAsync() },
                                onCancel = { appViewModel.cancelFormatting() },
                                onCopy = { },
                                onSearch = {
                                    editorRef.value?.let { view ->
                                        openSearchPanel(view)
                                    }
                                },
                                viewMode = viewMode,
                                onViewModeChange = onViewModeChange,
                                showViewToggle = showViewToggle
                            )
                        }
                        EditorViewMode.Tree -> {

                        }
                        EditorViewMode.Compare -> {
                            CompareToolbar(
                                text = compareText,
                                format = {
                                    appViewModel.formatCompareJsonAsync()
                                },
                                minify = {
                                    appViewModel.minifyCompareJsonAsync()
                                },
                                clear = {
                                    appViewModel.updateCompareText("")
                                    appViewModel.clearCompareDiffs()
                                },
                                swap = {
                                    appViewModel.swapCompareTexts()
                                },
                                copy = {
                                    clipboard.setText(AnnotatedString(appViewModel.compareText.value))
                                },
                                download = {
                                    FileExport.default.downloadJson(
                                        "jsonize.json",
                                        appViewModel.compareText.value
                                    )
                                }
                            )
                        }
                        EditorViewMode.Generator -> {
                            GeneratorToolbar(
                                appViewModel = appViewModel,
                                copy = {
                                    scope.launch {
                                        copyToClipboard(appViewModel.code.value)
                                    }
                                },
                                download = {
                                    scope.launch {
                                        downloadTextFile(appViewModel.codeFileName.value, appViewModel.code.value)
                                    }
                                }
                            )
                        }
                    }
                } else {
                    EditorToolbar(
                        text = localText,
                        isFormatting = formatting,
                        onFormat = { appViewModel.formatJsonAsync() },
                        onMinify = { appViewModel.minifyJsonAsync() },
                        onCancel = { appViewModel.cancelFormatting() },
                        onCopy = { },
                        onSearch = {
                            editorRef.value?.let { view ->
                                openSearchPanel(view)
                            }
                        },
                        viewMode = viewMode,
                        onViewModeChange = onViewModeChange,
                        showViewToggle = showViewToggle
                    )
                }
            }

            Column(
                modifier = Modifier
                    .fillMaxHeight()
                    .weight(1f)
            ) {
                when (effectiveViewMode) {
                    EditorViewMode.Tree -> {
                        Box(Modifier.weight(1f).fillMaxWidth()) {
                            JsonTreeView(parsedJson)
                        }
                    }
                    EditorViewMode.Compare -> {
                        Box(Modifier.weight(1f).fillMaxWidth()) {
                            CompareView(appViewModel, editorRef, 0.dp)
                        }
                    }
                    EditorViewMode.Generator -> {
                        Box(Modifier.weight(1f).fillMaxWidth()){
                            GeneratorPanel(appViewModel, text)
                        }
                    }
                    else -> {
                        MainEditorContent(modifier = Modifier.weight(1f).fillMaxWidth())
                    }
                }
                if (showViewToggle) {
                    Spacer(Modifier.height(6.dp))
                    Row(Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.spacedBy(6.dp)) {
                        ViewModeButton(
                            selected = viewMode == EditorViewMode.Code,
                            onClick = { onViewModeChange(EditorViewMode.Code) },
                            icon = Icons.Default.Edit,
                            description = "Code view"
                        )
                        ViewModeButton(
                            selected = viewMode == EditorViewMode.Tree,
                            onClick = { onViewModeChange(EditorViewMode.Tree) },
                            icon = Icons.Default.AccountTree,
                            description = "Tree view"
                        )
                        ViewModeButton(
                            selected = viewMode == EditorViewMode.Compare,
                            onClick = { onViewModeChange(EditorViewMode.Compare) },
                            icon = Icons.Default.Compare,
                            description = "Compare View"
                        )
                        ViewModeButton(
                            selected = viewMode == EditorViewMode.Generator,
                            onClick = { onViewModeChange(EditorViewMode.Generator) },
                            icon = Icons.Default.Code,
                            description = "Code Generator"
                        )
                    }
                }
            }
        }
    }
}
